export type BlogInlinePart =
  | { type: 'text'; value: string }
  | { type: 'link'; label: string; href: string; external?: boolean };

export type BlogParagraph = {
  parts: readonly BlogInlinePart[];
};

export type BlogSection = {
  heading?: string;
  paragraphs: readonly BlogParagraph[];
  bullets?: readonly string[];
  links?: readonly { label: string; href: string }[];
};

export type BlogAuthor = {
  name: string;
  role: string;
};

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  author: BlogAuthor;
  sections: readonly BlogSection[];
};

const t = (value: string): BlogInlinePart => ({ type: 'text', value });
const l = (label: string, href: string, external?: boolean): BlogInlinePart => ({
  type: 'link',
  label,
  href,
  external,
});
const p = (...parts: BlogInlinePart[]): BlogParagraph => ({ parts });
const blogCover = (slug: string) => `/images/blog/${slug}.jpg`;

export const BLOG_POSTS: readonly BlogPost[] = [
  {
    slug: 'transparent-capital-partnerships-2026',
    title: 'Why transparent capital partnerships matter in 2026',
    excerpt:
      'Hidden terms and opaque structures erode trust on both sides. Here is why clarity upfront is the foundation of every successful partnership.',
    category: 'Partnerships',
    date: '2026-07-18',
    readTime: '9 min read',
    image: blogCover('transparent-capital-partnerships-2026'),
    author: { name: 'Amara Okello', role: 'Head of Partnerships, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Capital partnerships have long been shaped by complexity, layered fees, unclear profit splits, and agreements that only make sense after lawyers decode them. For investors and entrepreneurs alike, that opacity creates friction before a single shilling moves.'),
          ),
          p(
            t('Hello Gwiji was built on a different premise: every term documented upfront, every communication auditable, and every transaction visible to both parties through a regulated wallet layer. If you are new to the platform, start with our '),
            l('two-layer approach overview', '/blog/two-layer-approach-explained'),
            t(' before diving into partnership mechanics.'),
          ),
        ],
      },
      {
        heading: 'Clarity builds confidence',
        paragraphs: [
          p(
            t('When investors can see exactly how returns are structured, they commit with conviction. When entrepreneurs understand the full cost of capital, not just the headline number, they make decisions that protect their vision long term.'),
          ),
          p(
            t('Transparent partnerships are not about removing negotiation. They are about replacing surprises with shared understanding. That shift alone can shorten deal cycles and reduce the emotional cost of fundraising.'),
          ),
        ],
        bullets: [
          'Terms are visible before capital is committed',
          'Profit-sharing schedules are agreed in writing early',
          'Both parties share a single source of truth for payouts',
        ],
      },
      {
        heading: 'What changes in practice',
        paragraphs: [
          p(
            t('Digital contracts stored immutably before funds move. Profit-sharing schedules encoded at agreement time. Real-time tracking so neither side is guessing about status or payouts.'),
          ),
          p(
            t('On Hello Gwiji, the '),
            l('venture layer', '/blog/two-layer-approach-explained'),
            t(' handles discovery and agreement, while the fintech layer handles deposits, holds, and distributions. Keeping those steps connected is what makes transparency operational, not just aspirational.'),
          ),
        ],
        links: [
          { label: 'How trust is built on-platform', href: '/blog/building-trust-investors-entrepreneurs' },
          { label: 'View the application', href: '/application' },
        ],
      },
      {
        heading: 'The cost of staying opaque',
        paragraphs: [
          p(
            t('Opaque deals do not fail loudly. They fail slowly, through missed milestones, disputed terms, and relationships that sour when one party feels misled. Investors remember the headache. Founders remember the distraction.'),
          ),
          p(
            t('Regulators and LPs are also raising the bar. In 2026, "we will sort out the details later" is a red flag, not a flex. Documentation upfront is becoming the baseline expectation for serious capital.'),
          ),
        ],
      },
      {
        heading: 'A checklist for both sides',
        paragraphs: [
          p(
            t('Whether you are raising or investing, use this as a starting point before you sign anything, on Hello Gwiji or elsewhere.'),
          ),
        ],
        bullets: [
          'Is the profit split documented in plain language?',
          'Are fees, holds, and release conditions defined upfront?',
          'Is there an auditable record of messages and agreed changes?',
          'Does the wallet or escrow layer match what was written in the contract?',
        ],
        links: [
          { label: 'Join the waitlist', href: '/waitlist' },
          { label: 'Privacy policy', href: '/privacy-policy' },
        ],
      },
      {
        heading: 'Where we go from here',
        paragraphs: [
          p(
            t('That is the standard we believe capital partnerships should meet in 2026, and the standard Hello Gwiji is building toward. Transparency is not a feature checklist. It is the environment in which good partnerships grow.'),
          ),
          p(
            t('We are rolling out platform access in phases. '),
            l('Get in touch', '/contact'),
            t(' to join the waitlist, explore '),
            l('how the product works', '/application'),
            t(', and follow the '),
            l('blog', '/blog'),
            t(' for updates as we launch across every device.'),
          ),
        ],
      },
    ],
  },
  {
    slug: 'building-trust-investors-entrepreneurs',
    title: 'Building trust between investors and entrepreneurs',
    excerpt:
      'Matching is only the beginning. Lasting partnerships require verified profiles, secure messaging, and terms both sides can stand behind.',
    category: 'Platform',
    date: '2026-06-24',
    readTime: '8 min read',
    image: blogCover('building-trust-investors-entrepreneurs'),
    author: { name: 'James Mwangi', role: 'Platform Lead, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Trust is the currency of every capital partnership. Without it, even the most promising venture and the most patient investor will struggle to move forward.'),
          ),
          p(
            t('Hello Gwiji approaches trust as infrastructure, not a marketing promise. Verified entrepreneur profiles, encrypted investor communications, and guided flows from discovery to agreement. For the full picture on how money moves after terms are set, read about '),
            l('transparent partnerships in 2026', '/blog/transparent-capital-partnerships-2026'),
            t('.'),
          ),
        ],
      },
      {
        heading: 'Verification from day one',
        paragraphs: [
          p(
            t('Every opportunity on the platform is vetted before it reaches an investor feed. Business plans, funding needs, and venture details are presented in structured profiles so due diligence starts with substance, not speculation.'),
          ),
          p(
            t('Verification is not a one-time badge. Profiles evolve as ventures hit milestones, update financials, and refine their ask. Investors see a living record, not a snapshot from a single pitch deck upload.'),
          ),
        ],
        bullets: [
          'Structured venture profiles with clear funding asks',
          'Document uploads tied to each opportunity',
          'Review checkpoints before listings go live',
        ],
      },
      {
        heading: 'Communication that leaves a trail',
        paragraphs: [
          p(
            t('Secure messaging keeps conversations inside the platform where they can be logged and referenced. When terms are agreed, there is a clear record of how you got there, protecting both parties if questions arise later.'),
          ),
          p(
            t('Off-platform negotiations are where misunderstandings multiply. Email threads get lost. Verbal promises fade. Keeping dialogue inside Hello Gwiji means the path from first hello to signed terms stays traceable.'),
          ),
        ],
        links: [
          { label: 'Two-layer product design', href: '/blog/two-layer-approach-explained' },
          { label: 'Terms & conditions', href: '/terms-and-conditions' },
        ],
      },
      {
        heading: 'From match to agreement',
        paragraphs: [
          p(
            t('Discovery is exciting. Paperwork is not. We designed guided flows so neither side has to guess the next step, express interest, negotiate, document, then move capital through the regulated wallet layer.'),
          ),
          p(
            t('That sequence matters. Agreement before transfer is the same principle behind every trustworthy marketplace, and it is especially critical when the asset is someone\'s company and someone else\'s savings.'),
          ),
        ],
      },
      {
        heading: 'What investors should look for',
        paragraphs: [
          p(
            t('Beyond returns, evaluate how a platform handles the boring parts: identity, messaging history, contract storage, and payout visibility. Those are the systems that protect you when a venture hits a rough quarter.'),
          ),
        ],
        bullets: [
          'Can you see the full negotiation history?',
          'Are wallet movements tied to documented terms?',
          'Is there a clear dispute or support path?',
        ],
        links: [
          { label: 'Join the waitlist', href: '/waitlist' },
          { label: 'See the app', href: '/application' },
        ],
      },
    ],
  },
  {
    slug: 'two-layer-approach-explained',
    title: "Hello Gwiji's two-layer approach explained",
    excerpt:
      'Venture matching and regulated fintech wallets work together so partnerships stay transparent from first hello to final payout.',
    category: 'Product',
    date: '2026-05-30',
    readTime: '10 min read',
    image: blogCover('two-layer-approach-explained'),
    author: { name: 'Sarah Kimani', role: 'Product Design, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Most platforms do one thing well, either they connect people or they move money. Hello Gwiji combines both in two deliberate layers that stay in sync.'),
          ),
          p(
            t('Splitting the problem this way is intentional. Relationship and terms live in one layer; custody and movement live in another. When they are disconnected, transparency breaks down, which is why we wrote about '),
            l('why transparent partnerships matter', '/blog/transparent-capital-partnerships-2026'),
            t(' as a foundation for everything we build.'),
          ),
        ],
      },
      {
        heading: 'Venture Layer',
        paragraphs: [
          p(
            t('Browse verified ventures. Express interest. Negotiate terms through secure messaging. Document agreements digitally before any capital changes hands.'),
          ),
          p(
            t('This layer is where relationships form and structures take shape, with clarity built into every step. Think of it as the workspace: profiles, conversations, and contracts in one place.'),
          ),
        ],
        bullets: [
          'Investor feeds filtered by sector, stage, and ticket size',
          'Interest signals and structured negotiation threads',
          'Digital agreements linked to each partnership',
        ],
        links: [{ label: 'Building trust on-platform', href: '/blog/building-trust-investors-entrepreneurs' }],
      },
      {
        heading: 'Fintech Layer',
        paragraphs: [
          p(
            t('Deposits, holds, releases, withdrawals, and profit distributions all happen inside a regulated wallet system. Every movement is tracked and visible to both parties.'),
          ),
          p(
            t('By keeping money inside the platform until terms are met, we reduce the gap between what was agreed and what actually happened. No shadow transfers. No "I thought you sent it" moments.'),
          ),
        ],
      },
      {
        heading: 'Why both layers must talk to each other',
        paragraphs: [
          p(
            t('A beautiful matching UI without payment integrity is a directory. A wallet without structured agreements is just another app. The value is in the handshake between them, when a release trigger in the fintech layer reflects a clause both parties signed in the venture layer.'),
          ),
          p(
            t('That integration is what we are shipping toward across '),
            l('MacBook, iPad, iOS, and Android', '/application'),
            t('. Same account, same history, same rules, regardless of device.'),
          ),
        ],
        links: [
          { label: 'View the application', href: '/application' },
          { label: 'Contact us', href: '/contact' },
        ],
      },
      {
        heading: 'Roadmap and access',
        paragraphs: [
          p(
            t('We are opening access in phases. Early waitlist members will see venture features first, followed by wallet capabilities as regulatory integrations go live. Follow the '),
            l('blog', '/blog'),
            t(' for product updates and read '),
            l('our vision from Nairobi', '/blog/nairobi-to-the-world'),
            t(' to understand where this is headed long term.'),
          ),
        ],
      },
    ],
  },
  {
    slug: 'nairobi-to-the-world',
    title: 'From Nairobi to the world: our vision',
    excerpt:
      'Rooted in Kenya, Hello Gwiji is building capital infrastructure that connects local ventures with investors who believe in transparent growth.',
    category: 'Company',
    date: '2026-05-04',
    readTime: '7 min read',
    image: blogCover('nairobi-to-the-world'),
    author: { name: 'Hello Gwiji Editorial', role: 'Company & culture' },
    sections: [
      {
        paragraphs: [
          p(
            t('Hello Gwiji started with a simple observation: talented entrepreneurs across Nairobi and the wider region often lack access to fair, transparent capital, while investors struggle to find vetted opportunities they can trust.'),
          ),
          p(
            t('We are building the bridge between those two worlds, with the same '),
            l('two-layer product architecture', '/blog/two-layer-approach-explained'),
            t(' we use everywhere else: structured matching plus regulated wallets.'),
          ),
        ],
      },
      {
        heading: 'Local roots, global standards',
        paragraphs: [
          p(
            t('Being based in Nairobi keeps us close to the ventures and investors we serve. It also keeps us honest about the constraints, aspirations, and regulatory realities that shape capital partnerships in emerging markets.'),
          ),
          p(
            t('Our ambition is not limited by geography. Transparent, guided partnerships should work anywhere people want to build together with integrity, whether the next deal is in Westlands or with a diaspora investor in London.'),
          ),
        ],
        bullets: [
          'Built for East African venture realities first',
          'Designed to scale with global compliance in mind',
          'Product language and flows tested with local founders',
        ],
      },
      {
        heading: 'The team and the mission',
        paragraphs: [
          p(
            t('We are operators and builders who have seen both sides of the table, founders chasing runway and investors chasing clarity. Hello Gwiji exists because the gap between those experiences should be narrower.'),
          ),
          p(
            t('If that resonates, '),
            l('join the waitlist', '/waitlist'),
            t(', explore '),
            l('the application preview', '/application'),
            t(', and dig into how we think about '),
            l('trust', '/blog/building-trust-investors-entrepreneurs'),
            t(' and '),
            l('transparency', '/blog/transparent-capital-partnerships-2026'),
            t(' on the platform.'),
          ),
        ],
        links: [
          { label: 'All blog articles', href: '/blog' },
          { label: 'Privacy policy', href: '/privacy-policy' },
        ],
      },
      {
        heading: 'What success looks like',
        paragraphs: [
          p(
            t('Success is not a vanity metric on a pitch deck. It is a founder who raised on clear terms, an investor who knew exactly what they were buying into, and a partnership that survived its first tough year because the foundation was documented from day one.'),
          ),
          p(
            t('That is the world we are working toward, starting here, starting now. Thank you for reading along.'),
          ),
        ],
      },
    ],
  },
  {
    slug: 'due-diligence-checklist-for-investors',
    title: 'A practical due diligence checklist for investors',
    excerpt:
      'Before you commit capital, run through these questions, from financial clarity to platform records, so every investment starts on solid ground.',
    category: 'Partnerships',
    date: '2026-08-01',
    readTime: '7 min read',
    image: blogCover('due-diligence-checklist-for-investors'),
    author: { name: 'Amara Okello', role: 'Head of Partnerships, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Due diligence is not about slowing deals down. It is about making sure the deals you do are worth your time, reputation, and capital. On Hello Gwiji, much of the groundwork is structured for you, but the final judgment still belongs to the investor.'),
          ),
          p(
            t('This checklist complements our broader view on '),
            l('transparent partnerships', '/blog/transparent-capital-partnerships-2026'),
            t(' and the '),
            l('trust infrastructure', '/blog/building-trust-investors-entrepreneurs'),
            t(' we build into every profile and conversation.'),
          ),
        ],
      },
      {
        heading: 'Before the first meeting',
        paragraphs: [
          p(
            t('Start with the venture profile itself. Is the funding ask specific? Are milestones realistic? Does the team explain how capital will be deployed, not just how much they need?'),
          ),
        ],
        bullets: [
          'Clear use-of-funds breakdown',
          'Documented ownership and cap table summary',
          'Sector fit with your stated investment thesis',
          'History of prior raises or traction metrics',
        ],
      },
      {
        heading: 'During negotiation',
        paragraphs: [
          p(
            t('Keep negotiations on-platform when possible. Secure messaging creates a record that protects both sides if terms evolve. Align on profit-sharing, hold conditions, and release triggers before any wallet transfer.'),
          ),
        ],
        links: [
          { label: 'Two-layer approach', href: '/blog/two-layer-approach-explained' },
          { label: 'Wallet & profit sharing', href: '/blog/wallet-layer-profit-sharing-explained' },
        ],
      },
      {
        heading: 'Before capital moves',
        paragraphs: [
          p(
            t('Confirm the digital agreement matches what you discussed. Verify wallet instructions, payout schedules, and dispute paths. If something feels vague, pause, clarity now prevents conflict later.'),
          ),
          p(
            t('Ready to explore vetted opportunities? '),
            l('Join the waitlist', '/waitlist'),
            t(' or '),
            l('preview the app', '/application'),
            t(' to see how due diligence fits into the Hello Gwiji flow.'),
          ),
        ],
      },
    ],
  },
  {
    slug: 'founder-guide-first-capital-raise',
    title: "Founder's guide to your first capital raise",
    excerpt:
      'Raising for the first time is daunting. Here is how to prepare your profile, tell your story, and negotiate terms that protect your vision.',
    category: 'Partnerships',
    date: '2026-07-28',
    readTime: '8 min read',
    image: blogCover('founder-guide-first-capital-raise'),
    author: { name: 'David Njoroge', role: 'Venture Programs, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Your first raise sets the tone for every partnership that follows. Founders who document clearly and negotiate honestly tend to attract investors who stay, not just investors who write one cheque and disappear.'),
          ),
          p(
            t('Hello Gwiji is designed to guide you from profile to agreement. If you have not read it yet, start with our '),
            l('foundational take on transparency', '/blog/transparent-capital-partnerships-2026'),
            t(' before you publish your venture listing.'),
          ),
        ],
      },
      {
        heading: 'Prepare your story',
        paragraphs: [
          p(
            t('Investors back people and problems before they back projections. Lead with the problem you solve, why you are the team to solve it, and what changes if you succeed.'),
          ),
        ],
        bullets: [
          'One-paragraph venture summary written in plain language',
          'Three measurable milestones for the next 12 months',
          'Honest assessment of risks and dependencies',
        ],
      },
      {
        heading: 'Structure your ask',
        paragraphs: [
          p(
            t('Be specific about amount, instrument, and use of funds. Vague asks signal vague planning. Tie every major expense line to an outcome investors can track.'),
          ),
          p(
            t('Use our '),
            l('due diligence checklist', '/blog/due-diligence-checklist-for-investors'),
            t(' as a mirror, if you cannot answer those questions confidently, investors will struggle too.'),
          ),
        ],
      },
      {
        heading: 'Close with clarity',
        paragraphs: [
          p(
            t('When you reach agreement, document everything before funds move. The '),
            l('fintech wallet layer', '/blog/wallet-layer-profit-sharing-explained'),
            t(' only works when the venture layer has done its job first.'),
          ),
        ],
        links: [
          { label: 'Join the waitlist', href: '/waitlist' },
          { label: 'Building trust on-platform', href: '/blog/building-trust-investors-entrepreneurs' },
        ],
      },
    ],
  },
  {
    slug: 'secure-messaging-capital-deals',
    title: 'Why secure messaging matters in capital deals',
    excerpt:
      'Off-platform chats create gaps in the record. Here is why we keep investor-founder conversations inside Hello Gwiji, and what that means for you.',
    category: 'Platform',
    date: '2026-07-08',
    readTime: '6 min read',
    image: blogCover('secure-messaging-capital-deals'),
    author: { name: 'James Mwangi', role: 'Platform Lead, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Capital deals are built on conversations, but conversations without a record are liabilities. A term agreed on WhatsApp at midnight is hard to enforce when memories diverge six months later.'),
          ),
          p(
            t('Hello Gwiji keeps messaging encrypted, logged, and tied to the venture context. That is part of the broader '),
            l('trust story', '/blog/building-trust-investors-entrepreneurs'),
            t(' we tell across the platform.'),
          ),
        ],
      },
      {
        heading: 'What on-platform messaging gives you',
        paragraphs: [
          p(
            t('Every thread is scoped to a specific opportunity. Investors and founders see the same history. Attachments, term revisions, and agreed changes stay linked to the deal, not scattered across inboxes.'),
          ),
        ],
        bullets: [
          'Encrypted delivery and storage',
          'Threads tied to venture profiles',
          'Audit-friendly conversation history',
        ],
      },
      {
        heading: 'When to move from chat to contract',
        paragraphs: [
          p(
            t('Messaging is for exploration and negotiation. Contracts are for commitment. The platform guides you from dialogue to documented agreement before the '),
            l('wallet layer', '/blog/two-layer-approach-explained'),
            t(' activates.'),
          ),
        ],
        links: [
          { label: 'Product architecture', href: '/blog/two-layer-approach-explained' },
          { label: 'Contact support', href: '/contact' },
        ],
      },
    ],
  },
  {
    slug: 'wallet-layer-profit-sharing-explained',
    title: 'How the wallet layer handles profit sharing',
    excerpt:
      'Agreements mean little if payouts are opaque. Here is how Hello Gwiji tracks deposits, holds, releases, and profit distributions in one regulated flow.',
    category: 'Product',
    date: '2026-06-12',
    readTime: '8 min read',
    image: blogCover('wallet-layer-profit-sharing-explained'),
    author: { name: 'Sarah Kimani', role: 'Product Design, Hello Gwiji' },
    sections: [
      {
        paragraphs: [
          p(
            t('Profit sharing is where partnerships get tested. The venture layer sets expectations; the wallet layer proves they were met. Hello Gwiji connects both so neither side operates on faith alone.'),
          ),
          p(
            t('This article goes deeper on the fintech half of our '),
            l('two-layer model', '/blog/two-layer-approach-explained'),
            t(', read that first if you are new to the platform.'),
          ),
        ],
      },
      {
        heading: 'Deposits and holds',
        paragraphs: [
          p(
            t('Capital enters through regulated wallet deposits. Funds can be held against agreed milestones before release, giving investors confidence and founders a clear target to hit.'),
          ),
        ],
        bullets: [
          'Deposits logged against specific partnerships',
          'Hold rules encoded from signed agreements',
          'Release triggers visible to both parties',
        ],
      },
      {
        heading: 'Distributions and withdrawals',
        paragraphs: [
          p(
            t('When profit-sharing schedules kick in, both sides see the calculation and the transfer. No manual spreadsheets. No "trust me, the wire is coming." Just auditable movement tied to terms you already agreed.'),
          ),
        ],
        links: [
          { label: 'Transparent partnerships', href: '/blog/transparent-capital-partnerships-2026' },
          { label: 'View the application', href: '/application' },
        ],
      },
      {
        heading: 'Built for accountability',
        paragraphs: [
          p(
            t('We are integrating wallet capabilities in phases alongside venture features. '),
            l('Join the waitlist', '/waitlist'),
            t(' for early access and follow the '),
            l('blog', '/blog'),
            t(' for launch updates.'),
          ),
        ],
      },
    ],
  },
  {
    slug: 'east-africa-venture-landscape-2026',
    title: 'East Africa venture landscape in 2026',
    excerpt:
      'From Nairobi to Kampala to Kigali, founders are building faster than capital infrastructure can keep up. Here is what we are seeing, and where Hello Gwiji fits.',
    category: 'Company',
    date: '2026-04-22',
    readTime: '7 min read',
    image: blogCover('east-africa-venture-landscape-2026'),
    author: { name: 'Hello Gwiji Editorial', role: 'Company & culture' },
    sections: [
      {
        paragraphs: [
          p(
            t('East Africa\'s venture scene in 2026 is defined by ambition meeting infrastructure gaps. Founders are solving real problems at scale, but capital still moves through channels built for a different era.'),
          ),
          p(
            t('Hello Gwiji was born in Nairobi because we saw that gap firsthand. Our '),
            l('vision from Nairobi to the world', '/blog/nairobi-to-the-world'),
            t(' is not sloganeering, it is an operating principle.'),
          ),
        ],
      },
      {
        heading: 'What founders are telling us',
        paragraphs: [
          p(
            t('Runway pressure is real. So is the frustration of opaque term sheets and informal agreements that fall apart under stress. Founders want capital partners who document clearly and move at startup speed.'),
          ),
        ],
        bullets: [
          'Demand for transparent profit-sharing structures',
          'Interest in digital agreements before transfer',
          'Appetite for investor profiles they can evaluate upfront',
        ],
      },
      {
        heading: 'What investors are telling us',
        paragraphs: [
          p(
            t('Investors, local and diaspora, want vetted deal flow without sacrificing diligence depth. They need platforms that respect regulation while reducing friction. That is the balance we are building toward.'),
          ),
        ],
        links: [
          { label: 'Due diligence checklist', href: '/blog/due-diligence-checklist-for-investors' },
          { label: "Founder's first raise guide", href: '/blog/founder-guide-first-capital-raise' },
        ],
      },
      {
        heading: 'Looking ahead',
        paragraphs: [
          p(
            t('The next chapter is not just more capital, it is better capital infrastructure. If you are building or investing in the region, '),
            l('get in touch', '/contact'),
            t('. We would love to hear what you are working on.'),
          ),
        ],
      },
    ],
  },
] as const;

export function getBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((post) => post.slug === slug);
}

export function formatBlogDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
